/**
 * How a body paragraph is named for the engine: `_word_word_word` drawn from
 * `PARAGRAPH_ID_WORDS`, for example `_result_prev_signed`.
 *
 * WHY words and not an id
 *   A paragraph id is named by every operation of every action plan and quoted
 *   back in every refusal, so a language model reads and writes it constantly.
 *   The `_dioptra_p_` shape prod's Python minters write is about twenty-two
 *   tokens each time it appears; three screened words are three.
 *
 * The recognizer and the minter live here together so they can never disagree
 * about what a paragraph id looks like.
 *
 * `RETIRED_PARAGRAPH_BOOKMARK_PREFIXES` is the migration, not an alternative
 * scheme: a document named by prod's minters carries `_dioptra_p_` names, and
 * the naming pass strips those before it stamps word ids, so a paragraph never
 * carries two shapes at once.
 */
import { PARAGRAPH_ID_WORDS } from "./paragraph-id-words";
import { LEGACY_PARAGRAPH_BOOKMARK_PREFIX, PARAGRAPH_BOOKMARK_SHAPE } from "./ooxml-names";

const WORDS_PER_PARAGRAPH_ID = 3;
/** `2^32`, the number of values a `Uint32` can take; the rejection bound below is computed from it. */
const UINT32_VALUE_COUNT = 2 ** 32;

/** Paragraph bookmark shapes the naming pass removes before it names a document; they are not names this engine ever writes. */
export const RETIRED_PARAGRAPH_BOOKMARK_PREFIXES: readonly string[] = [LEGACY_PARAGRAPH_BOOKMARK_PREFIX];

/** True for a name this engine treats as a paragraph id; every other bookmark is a user's or Word's own. */
export function isParagraphBookmarkName(bookmarkName: string): boolean {
    return PARAGRAPH_BOOKMARK_SHAPE.test(bookmarkName);
}

/**
 * A uniformly random index below `exclusiveLimit` from the CSPRNG, by
 * rejection: a draw at or above the largest multiple of `exclusiveLimit` that
 * fits in a Uint32 is thrown away, so no index is favoured by the remainder.
 *
 * EXAMPLE
 *   exclusiveLimit          = 800
 *   floor(2^32 / 800) * 800 = 4294966400 -> draws in [0, 4294966400) are kept, the last 896 values redrawn
 *   kept draw 1601          -> 1601 % 800 = 1
 */
function uniformRandomIndex(exclusiveLimit: number): number {
    const largestUnbiasedDrawExclusive = Math.floor(UINT32_VALUE_COUNT / exclusiveLimit) * exclusiveLimit;
    const drawBuffer = new Uint32Array(1);
    let draw: number;
    do {
        globalThis.crypto.getRandomValues(drawBuffer);
        draw = drawBuffer[0];
    } while (draw >= largestUnbiasedDrawExclusive);
    return draw % exclusiveLimit;
}

/**
 * EXAMPLE
 *   draws  = 611, 540, 690   (indexes into PARAGRAPH_ID_WORDS)
 *   words  = "result", "prev", "signed"
 *   name   = "_result_prev_signed"
 */
function paragraphBookmarkNameCandidate(): string {
    const chosenWords: string[] = [];
    for (let wordPosition = 0; wordPosition < WORDS_PER_PARAGRAPH_ID; wordPosition += 1) chosenWords.push(PARAGRAPH_ID_WORDS[uniformRandomIndex(PARAGRAPH_ID_WORDS.length)]);
    return `_${chosenWords.join("_")}`;
}

/**
 * A paragraph id that is not in `namesInUse`. The caller passes every bookmark
 * name already in the document: `Range.insertBookmark(name)` deletes an existing
 * bookmark of the same name first, so a collision would move another paragraph's
 * id.
 *
 * WHEN the first candidate is free (the case every time in practice: 737^3 names against a few thousand in a document)
 *   CONSEQUENCE
 *     one draw, returned as is.
 * WHEN a candidate collides
 *   CONSEQUENCE
 *     redraw; the loop ends because the name space is vastly larger than any document's bookmark set.
 */
export function freshParagraphBookmarkName(namesInUse: ReadonlySet<string>): string {
    let candidateName = paragraphBookmarkNameCandidate();
    while (namesInUse.has(candidateName)) candidateName = paragraphBookmarkNameCandidate();
    return candidateName;
}
